import type { TableColumnType } from 'ant-design-vue'
import { BASE_COLUMNS, type IColumns } from '-/Base/BdTable'
import { useDictStore } from '@/store/modules/dict'
import { ID_COLUMN } from '@/constant/table'
import { useActionKey } from '@/plugins/actionKey'
import { BdBtn } from '../Common/BdBtn'
import { AuthLink } from '../Common/AuthLink'
import { PensionInsuranceReviewStatus, SimpleReviewStatus, ZchReviewStatus } from './Status'
import { HouseStatusDisplay, JtZchRenYuanNameDisplay, JtZchSignStatusDisplay, TicketMatchResultDisplay, TicketModeDisplay } from './CommonDisplay'
import NameIdDisplay from './NameIdDisplay.vue'
import { BaseBooleanStatusRender } from '@/constant'
import type { ConditionBoxConfig } from '../Base/ConditionBox'
import dayjs from 'dayjs'
import JtZchResettleDisplay from './JtZchResettleDisplay.vue'
import { JtZchSocialSecurityCol } from '@/views/JtZch/components/common'
import AddressSelect from './AddressSelect.vue'
import { ZCH_REVIEW_STATUS_OPTIONS } from '@/constant/biz'
import { API_JT_PROJ_SIMPLE_LIST } from '@/api/jtProj'
import { API_GY_PROJ_SIMPLE_LIST } from '@/api/gyProj'
const $AK = useActionKey()
interface RenderActionColOptions extends Partial<TableColumnType> {
  editPermission?: string
  deletePermission?: string
  onEdit?: (record: any) => void
  onDelete?: (record: any) => void
}
/**
 * 操作列
 */
export const renderActionCol = (options: RenderActionColOptions = {}): TableColumnType => {
  const { fixed = 'right', editPermission, deletePermission, onEdit, onDelete } = options
  return {
    title: '操作',
    dataIndex: 'action',
    width: 120,
    fixed,
    customRender({ record }) {
      return (
        <>
          <BdBtn type='link' size='small' v-permission={editPermission} onClick={() => onEdit?.(record)}>
            编辑
          </BdBtn>
          <BdBtn type='link' danger size='small' v-permission={deletePermission} onClick={() => onDelete?.(record)}>
            删除
          </BdBtn>
        </>
      )
    }
  }
}
/**
 * 集体土地项目-被征地农民养老保险-表格
 */
export const renderJtProjPensionInsuranceCols = () => [
  {
    title: '保险名称',
    dataIndex: 'name',
    width: 200
  },
  {
    title: '提交人',
    dataIndex: 'createAdminUserObj',
    width: 180,
    customRender({ text }) {
      return text?.loginName
    }
  },
  {
    title: '提交时间',
    dataIndex: 'createdAt',
    sorter: true,
    width: 180
  },
  {
    title: '状态',
    dataIndex: 'shenHeStatus',
    width: 180,
    customRender({ text }) {
      return <PensionInsuranceReviewStatus value={text} />
    }
  },
  {
    title: '审核人',
    dataIndex: 'lastShenHeName',
    width: 180,
    customRender({ record }) {
      return record.lastShenHe?.createAdminUserObj.loginName
    }
  },
  {
    title: '审核时间',
    dataIndex: 'lastShenHeTime',
    width: 180,
    customRender({ record }) {
      return record.lastShenHe?.createdAt
    }
  }
]
/**
 *
 * 集体土地项目-资金监审-表格
 */
export const renderJtProjFundAuditCols = () => [
  {
    title: '监审内容名称',
    dataIndex: 'name',
    width: 400
  },
  // {
  //   title: '监审类型',
  //   dataIndex: 'capitalExamineType',
  //   width: 150,
  //   customRender({ text }) {
  //     const dictStore = useDictStore()
  //     return dictStore.word.get(text)?.label || text
  //   }
  // },
  {
    title: '监审金额(元)',
    dataIndex: 'capitalExaminePrice',
    width: 180
  },
  {
    title: '状态',
    dataIndex: 'shenHeStatus',
    width: 180,
    customRender({ text }) {
      return <SimpleReviewStatus value={text} />
    }
  },
  {
    title: '发起人',
    dataIndex: 'createAdminUserObj',
    width: 180,
    customRender({ text }) {
      return text?.loginName
    }
  },
  {
    title: '发起时间',
    dataIndex: 'createdAt',
    sorter: true,
    width: 180
  }
]

/**
 * 集体土地项目-安置监审-表格
 */
export const renderJtProjResettleAuditCols = () => [
  {
    title: '监审内容名称',
    dataIndex: 'name',
    width: 400
  },
  // {
  //   title: '监审类型',
  //   dataIndex: 'placeExamineType',
  //   width: 150,
  //   customRender({ text }) {
  //     const dictStore = useDictStore()
  //     return dictStore.word.get(text)?.label || text
  //   }
  // },
  {
    title: '监审金额(元)',
    dataIndex: 'placeExaminePrice',
    width: 180
  },
  {
    title: '状态',
    dataIndex: 'shenHeStatus',
    width: 180,
    customRender({ text }) {
      return <SimpleReviewStatus value={text} />
    }
  },
  {
    title: '发起人',
    dataIndex: 'createAdminUserObj',
    width: 180,
    customRender({ text }) {
      return text?.loginName
    }
  },
  {
    title: '发起时间',
    dataIndex: 'createdAt',
    sorter: true,
    width: 180
  }
]

/**
 * 集体土地项目-征拆户-征拆户编号-显示列
 */
export const renderJtZchCodeDisplayCol = (opt: TableCustomRenderOpt) => {
  if (opt.record?.isPublicOffice) {
    return (
      <div title='该征拆户内含公职人员' class='color-error'>
        ★ {opt.text}
      </div>
    )
  }
  return <div>{opt.text}</div>
}

/**
 * 集体土地项目-征拆户-户主姓名显示列
 */
export const renderJtZchHuZhuXingMingCol = (opt: TableCustomRenderOpt) => {
  const { collectiveOwnedMember = [] } = opt.record
  return collectiveOwnedMember
    .reduce((prev: string[], curr: any) => {
      if (curr.isOwner) prev.push(curr.userName)
      return prev
    }, [])
    .join('')
}

/**
 * 集体土地项目-征拆户-家庭成员显示列
 */
export const renderJtZchJiaTingChengYuanCol = (opt: TableCustomRenderOpt) => {
  const { member = [] } = opt.record
  return member
    .reduce((prev: string[], curr: any) => {
      if (!curr.isOwner) prev.push(curr.userName)
      return prev
    }, [])
    .join('，')
}

/**
 * 集体土地项目-征拆户-人员安置
 */
export const renderJtZchRenYuanAnZhiCol = (opt: TableCustomRenderOpt) => {
  // 应安置人数：
  const memberCount = opt.record?.member?.length || 0
  // 已安置人数：
  const placedCount = opt.record?.placedCount || 0
  // 安置率：
  const placedPer = memberCount ? ((placedCount / memberCount) * 100).toFixed(2) + ' %' : '-'
  return (
    <div>
      <div>应安置人数：{memberCount ?? '-'}人</div>
      <div>已安置人数：{placedCount ?? '-'}人</div>
      <div>安置率：{placedPer}</div>
    </div>
  )
}

/**
 * 集体土地项目-征拆户-安置情况
 */
export const renderJtZchAnZhiQingKuangCol = (opt: TableCustomRenderOpt) => {
  const { isCommonPlan } = opt.record
  const res: any[] = []
  if (isCommonPlan) {
    res.push(
      <div>
        <h1>统规统建安置</h1>
      </div>
    )
  }
}

/**
 * 集体土地项目-征拆户-人员信息-姓名
 */
export const renderJtZchRenYuanNameCol = (opt: TableCustomRenderOpt) => {
  return <JtZchRenYuanNameDisplay name={opt.record.userName} isOwner={opt.record.isOwner} />
}

// ===================== 通用表格列配置 =====================

/**
 * 所属项目/ID列 - 使用 NameIdDisplay 组件
 * @param projectKey 项目数据的字段名，如 'project' 或 'cproject'
 */
export const renderProjectIdCol = (projectKey: string = 'project'): IColumns => ({
  title: '所属项目/ID',
  dataIndex: projectKey,
  width: 300,
  customRender({ text }) {
    return <NameIdDisplay id={text?.id} title={text?.projectName} />
  }
})

/**
 * 所属乡镇列
 */
export const renderStationCol = (): IColumns => ({
  title: '所属乡镇',
  dataIndex: 'createAdminStationObj',
  width: 200,
  customRender({ text }) {
    return text?.stationName
  }
})

/**
 * 审核状态列 - 征拆户审核状态
 */
export const renderZchReviewStatusCol = (): IColumns => ({
  title: '审核状态',
  dataIndex: 'shenHeStatus',
  width: 200,
  customRender({ text }) {
    return <ZchReviewStatus value={text} />
  }
})

/**
 * 发起人/提交人列
 */
export const renderCreatorCol = (title: string = '发起人'): IColumns => ({
  title,
  dataIndex: 'createAdminUserObj',
  width: 180,
  customRender({ text }) {
    return text?.loginName
  }
})

/**
 * 发起时间/创建时间列
 */
export const renderCreatedAtCol = (title: string = '发起时间', sorter: boolean = true): IColumns => ({
  title,
  dataIndex: 'createdAt',
  sorter,
  width: 180
})

/**
 * 档案编号列 - 带链接跳转
 * @param routeName 路由名称
 * @param getParams 获取路由参数的函数
 */
export const renderArchiveIdCol = (routeName: string, getParams?: (record: any) => Record<string, any>): IColumns => ({
  title: '档案编号',
  dataIndex: 'id',
  width: 200,
  customRender({ record, text }) {
    const params = getParams ? getParams(record) : { id: record.id, pId: record.projectId }
    // TODO： 防止参数缺失导致路由跳转异常
    if (params.id && params.pId) {
      return (
        <AuthLink blank name={routeName} to={{ params }}>
          {text}
        </AuthLink>
      )
    }
    return text
  }
})

/**
 * 产权人姓名列 - 拼接 member 数组
 * @param memberKey member 数据的字段名
 */
export const renderMemberNamesCol = (memberKey: string = 'member'): IColumns => ({
  title: '产权人姓名',
  dataIndex: memberKey,
  width: 200,
  customRender({ text = [] }) {
    return text
      .reduce((prev: string, curr: any) => {
        return prev + curr.userName + '、'
      }, '')
      .slice(0, -1)
  }
})

/**
 * 所属项目列 - 仅显示项目名称
 * @param projectKey 项目数据的字段名
 */
export const renderProjectNameCol = (projectKey: string = 'project'): IColumns => ({
  title: '所属项目',
  dataIndex: projectKey,
  width: 200,
  customRender({ text }) {
    return text?.projectName
  }
})

/**
 * 所属区域列
 */
export const renderAreaCol = (): IColumns => ({
  title: '所属区域',
  dataIndex: 'createAdminStationObj',
  width: 200,
  customRender({ text }) {
    return text?.stationName
  }
})

/**
 * 补偿总金额列
 */
export const renderTotalPriceCol = (): IColumns => ({
  title: '补偿总金额',
  dataIndex: 'totalPrice',
  width: 200
})

/**
 * 国有土地项目-房屋情况
 */
export const renderGyProjHouseSituationCol = (opt: TableCustomRenderOpt) => {
  const { record } = opt
  return (
    <div>
      <div>
        <span style='width:100px;display:inline-block'> 住宅：</span>
        {record?.zhuzhaiCount ?? '-'} 套，{record?.zhuzhaiSize ?? '-'} 平方米
      </div>
      <div>
        <span style='width:100px;display:inline-block'>门市：</span>
        {record?.menshiCount ?? '-'} 套，{record?.menshiSize ?? '-'} 平方米
      </div>
      <div>
        <span style='width:100px;display:inline-block'>非经营营业房：</span>
        {record?.feijingyingCount ?? '-'} 套，{record?.feijingyingSize ?? '-'} 平方米
      </div>
      <div>
        <span style='width:100px;display:inline-block'>其他：</span>
        {record?.qitaCount ?? '-'} 套，{record?.qitaSize ?? '-'} 平方米
      </div>
    </div>
  )
}

/**
 * 国有土地项目-项目类型
 */
export const renderGyProjTypeCol = (): IColumns => ({
  title: '项目类型',
  dataIndex: 'projectStateOwnedType',
  width: 200,
  isDict: true
})

/**
 * 国有土地项目-征收面积
 */
export const renderGyProjZhengShouAreaCol = (): IColumns => ({
  title: '征收面积',
  dataIndex: 'projectFilesSumTotalArea',
  width: 200,
  customRender({ text }) {
    return `${text ?? '-'} 亩`
  }
})

/**
 * 国有土地项目-征拆户数
 */
export const renderGyProjZchHuShuCol = (): IColumns => ({
  title: '征拆户数',
  dataIndex: 'projectFilesSumTotalNumber',
  width: 200,
  customRender({ text }) {
    return `${text ?? '-'} 户`
  }
})

/**
 * 国有土地征拆户-房屋信息
 */
export const renderGyZchHouseInfoCol = (opt: TableCustomRenderOpt) => {
  const dictStore = useDictStore()
  const { house = [] } = opt.record
  return house.length ? (
    <div>
      {house.map((item: any, index: number) => (
        <div>{`房屋${index + 1}(${dictStore.word.get(item.houseType)?.label})：${item.houseArea}平方米`}</div>
      ))}
    </div>
  ) : null
}

/**
 * 房源列表列
 */
export const renderHouseListCols = (): IColumns[] => [
  ID_COLUMN,
  {
    title: '所属小区',
    dataIndex: 'community',
    width: 250,
    customRender({ text }) {
      return text ? (
        <AuthLink blank permission={$AK.COMMUNITY.DETAIL} name={$AK.COMMUNITY.DETAIL} to={{ params: { id: text.id } }}>
          {text?.communityName ?? '-'}
        </AuthLink>
      ) : null
    }
  },
  {
    title: '房号',
    dataIndex: 'houseNumber',
    width: 150
  },
  {
    title: '户型',
    dataIndex: 'communityType',
    width: 200,
    customRender({ text }) {
      return text?.houseTypeName
    }
  },
  {
    title: '房屋性质',
    dataIndex: 'houseNatureType',
    isDict: true,
    width: 200
  },
  {
    title: '状态',
    dataIndex: 'state',
    width: 150,
    customRender({ record }) {
      return <HouseStatusDisplay isPlaced={record.isPlaced} isSet={record.isSet} />
    }
  },
  {
    title: '安置人员',
    dataIndex: 'member',
    width: 300,
    customRender({ text }) {
      if (text?.length) {
        const { userName, userIdCard, householdId, projectId } = text[0]
        return (
          <div>
            <div>
              姓名：
              <AuthLink blank name={$AK.JT_ZCH.DETAIL} to={{ params: { id: householdId, pId: projectId } }}>
                {userName ?? '-'}
              </AuthLink>
            </div>
            <div>身份证号：{userIdCard ?? '-'}</div>
          </div>
        )
      }
    }
  },
  ...BASE_COLUMNS
]

/**
 * 房源查询列表列
 */
export const renderHouseListConditionCols = (): ConditionBoxConfig => [
  {
    title: '所属小区',
    dataIndex: 'community.communityName',
    atom: 'like'
  },
  {
    title: '房号',
    dataIndex: 'houseNumber',
    atom: 'like'
  }
]
/**
 * 房票放款明细列表列
 * 列顺序：基础信息 -> 人员信息 -> 房产信息 -> 放款信息 -> 金额信息 -> 状态信息
 */
export const renderTicketLoanCols = (withZch = true): IColumns[] => {
  // 征拆户列（withZch=true时插入）
  const zchCols: IColumns[] = [
    {
      title: '征拆户档案编号',
      dataIndex: 'householdId',
      width: 180,
      customRender({ text, record }) {
        return text ? (
          <AuthLink blank name={$AK.JT_ZCH.DETAIL} to={{ params: { id: text, pId: record.projectId } }}>
            {text}
          </AuthLink>
        ) : null
      }
    }
  ]
  // 还款计划列（withZch=false时插入）
  const priceCols: IColumns[] = [
    {
      title: '还款月份',
      dataIndex: 'month',
      width: 120,
      customRender({ text }) {
        return text ? dayjs(text).format('YYYY-MM') : null
      }
    },
    {
      title: '本期还款金额',
      dataIndex: 'benQiJinE',
      width: 140
    }
  ]

  const res: IColumns[] = [
    ID_COLUMN,
    // ===== 基础信息 =====
    {
      title: '房票模式',
      dataIndex: 'fangPiaoMoshi',
      width: 120,
      customRender({ text }) {
        return <TicketModeDisplay value={text} />
      }
    },
    {
      title: '房票匹配结果',
      dataIndex: 'guanlianfangpiao',
      width: 120,
      customRender({ record }) {
        return <TicketMatchResultDisplay matched={!!record?.householdId} />
      }
    },
    // ===== 人员信息 =====
    {
      title: '承担人姓名',
      dataIndex: 'name',
      width: 120
    },
    {
      title: '承担人身份证号',
      dataIndex: 'idcard',
      width: 180
    },
    // ===== 房产信息 =====
    {
      title: '所属区域',
      dataIndex: 'suoshuquyu',
      width: 120
    },
    {
      title: '小区名称',
      dataIndex: 'louPanMingChen',
      width: 200
    },
    {
      title: '房号',
      dataIndex: 'fangHao',
      width: 120
    },
    // ===== 放款信息 =====
    {
      title: '放款银行',
      dataIndex: 'daiKuanHang',
      width: 150
    },
    {
      title: '还款账号',
      dataIndex: 'daiKuanZhangHao',
      width: 180
    },
    {
      title: '放款期限开始日期/第一期还款日期',
      dataIndex: 'startTime',
      width: 160
    },
    {
      title: '第二期还款日期',
      dataIndex: 'secondTime',
      width: 160
    },
    {
      title: '放款期限结束日期/第三期还款日期',
      dataIndex: 'endTime',
      width: 160
    },
    // ===== 金额信息 =====
    {
      title: '放款金额',
      dataIndex: 'zongJinE',
      width: 140
    },
    {
      title: '首付款',
      dataIndex: 'shouFuJinE',
      width: 140
    },
    {
      title: '首期还款金额/第一期还款金额',
      dataIndex: 'shouQiJinE',
      width: 140
    },
    {
      title: '期中还款金额/第二期还款金额',
      dataIndex: 'meiQiJinE',
      width: 140
    },
    {
      title: '末期还款金额/第三期还款金额',
      dataIndex: 'moQiJinE',
      width: 140
    },
    {
      title: '总待还金额',
      dataIndex: 'zongDaiHuan',
      width: 140
    },
    // ===== 状态信息 =====
    {
      title: '是否提前还款',
      dataIndex: 'isTiQian',
      width: 120,
      customRender({ text }) {
        return BaseBooleanStatusRender(text)
      }
    },
    {
      title: '是否可能提前还款',
      dataIndex: 'isTiQianMaybe',
      width: 160,
      customRender({ text }) {
        return BaseBooleanStatusRender(text)
      }
    }
  ]

  // 根据参数在第3个位置插入不同列
  if (withZch) {
    res.splice(3, 0, ...zchCols)
  } else {
    res.splice(3, 0, ...priceCols)
  }
  return res
}
/**
 * 集体征拆户列
 */
export const renderJtZchCols = (withProj = true) => {
  const res: IColumns[] = [
    renderArchiveIdCol($AK.JT_ZCH.DETAIL),
    {
      title: '征拆户编号',
      dataIndex: 'zhengchaihubianhao',
      width: 200,
      sorter: true,
      customRender: renderJtZchCodeDisplayCol
    },
    {
      title: '户主姓名',
      dataIndex: 'memberOwner',
      width: 200,
      customRender({ text }) {
        return text?.userName
      }
    },
    {
      title: '家庭成员',
      dataIndex: 'jiaTingChengYuan',
      width: 200,
      customRender: renderJtZchJiaTingChengYuanCol
    },
    renderAreaCol(),
    {
      title: '所属村组',
      dataIndex: 'village',
      width: 200
    },
    // renderProjectNameCol('cproject'),
    renderZchReviewStatusCol(),
    {
      title: '家庭人口数',
      dataIndex: 'collectiveOwnedMemberCount',
      width: 200,
      customRender({ record }) {
        return record?.member?.length ?? '-'
      }
    },
    {
      title: '签约状态',
      dataIndex: 'signFiles',
      width: 250,
      customRender({ text }) {
        return <JtZchSignStatusDisplay signFiles={text} />
      }
    },
    renderTotalPriceCol(),
    {
      title: '人员安置',
      dataIndex: 'placedMemberCount',
      width: 300,
      customRender: renderJtZchRenYuanAnZhiCol
    },
    {
      title: '安置情况',
      dataIndex: 'memberCurrency',
      width: 400,
      customRender({ record }) {
        return <JtZchResettleDisplay record={record} />
      }
    },
    {
      title: '社保情况',
      dataIndex: 'memberSocial',
      width: 200,
      customRender({ record }) {
        return <JtZchSocialSecurityCol data={record.member} />
      }
    }
  ]

  if (withProj) {
    res.splice(4, 0, renderProjectNameCol('cproject'))
  }
  return res
}

/**
 * 集体征拆户查询
 */
export const renderJtZchConditionCols = (withProj = true): ConditionBoxConfig => {
  // 项目相关条件
  const projConditions: ConditionBoxConfig = withProj
    ? [
        {
          title: '项目',
          dataIndex: 'projectId',
          atom: 'equal',
          type: 'select',
          dict: {
            data: API_JT_PROJ_SIMPLE_LIST,
            nameKey: 'projectName',
            valueKey: 'id'
          }
        },
        {
          title: '项目名称',
          dataIndex: 'project.projectName',
          atom: 'like'
        }
      ]
    : []

  // 基础信息条件
  const res: ConditionBoxConfig = [
    { title: '征拆户编号', dataIndex: 'zhengchaihubianhao', atom: 'like' },
    ...projConditions,
    {
      title: '档案编号',
      dataIndex: 'id',
      atom: 'like'
    },
    {
      title: '姓名',
      dataIndex: 'member.userName',
      atom: 'like'
    },
    {
      title: '身份证号',
      dataIndex: 'member.userIdCard',
      atom: 'like'
    },
    {
      title: '区域',
      dataIndex: 'project*projectPositions.positionAddressType',
      atom: 'equal',
      render({ modelRef }) {
        return <AddressSelect v-model:value={modelRef['project*projectPositions.positionAddressType']} />
      }
    },
    {
      title: '审核状态',
      dataIndex: 'shenHeStatus',
      atom: 'equal',
      type: 'select',
      dict: {
        data: ZCH_REVIEW_STATUS_OPTIONS
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      atom: 'between',
      type: 'dateTimeRangePicker'
    },
    {
      title: '房票安置',
      dataIndex: 'isTicket',
      atom: 'equal',
      type: 'numberRadio'
    },
    {
      title: '统规统建安置',
      dataIndex: 'isCommonPlan',
      atom: 'equal',
      type: 'numberRadio'
    },
    {
      title: '货币化安置',
      dataIndex: 'isCurrency',
      atom: 'equal',
      type: 'numberRadio'
    },
    {
      title: '统规自建安置',
      dataIndex: 'isPersonPlan',
      atom: 'equal',
      type: 'numberRadio'
    },
    {
      title: '签约',
      dataIndex: 'signFiles',
      atom: 'equal',
      type: 'numberRadio'
    },
    {
      title: '打款',
      dataIndex: 'ticketTime',
      atom: 'equal',
      type: 'numberRadio'
    }
  ]
  return res
}

/**
 * 国有土地征拆户列
 */
export const renderGyZchCols = (withProj = true): IColumns[] => {
  const dictStore = useDictStore()
  const projCols: IColumns[] = withProj
    ? [
        renderProjectNameCol(),
        {
          title: '项目类型',
          dataIndex: 'project.projectStateOwnedType',
          width: 200,
          customRender({ record }) {
            return dictStore.word.get(record?.project?.projectStateOwnedType)?.label
          }
        }
      ]
    : []
  const res: IColumns[] = [
    renderArchiveIdCol($AK.GY_ZCH.DETAIL, record => ({ id: record.id, pId: record?.project?.id })),
    renderMemberNamesCol(),
    ...projCols,
    renderZchReviewStatusCol(),
    {
      title: '打款情况',
      dataIndex: 'otherStartTime',
      width: 250,
      customRender({ text }) {
        if (text) {
          return (
            <div>
              <div>已打款</div>
              <div>{`打款时间：${text}`}</div>
            </div>
          )
        } else {
          return '未打款'
        }
      }
    },
    {
      title: '签约状态',
      dataIndex: 'otherSignTime',
      width: 250,
      customRender({ text }) {
        if (text) {
          return (
            <div>
              <div>已签约</div>
              <div>{`签约时间：${text}`}</div>
            </div>
          )
        } else {
          return '未签约'
        }
      }
    },
    renderTotalPriceCol(),
    {
      title: '房屋信息',
      dataIndex: 'house',
      width: 300,
      customRender: renderGyZchHouseInfoCol
    },
    {
      title: '搬离情况',
      dataIndex: 'otherOutTime',
      width: 250,
      customRender({ text }) {
        if (text) {
          return (
            <div>
              <div>已搬离</div>
              <div>{`搬离时间：${text}`}</div>
            </div>
          )
        } else {
          return '未搬离'
        }
      }
    }
  ]
  return res
}

/**
 * 国有土地征拆户查询
 */
export const renderGyZchConditionCols = (withProj = true): ConditionBoxConfig => {
  const dictStore = useDictStore()
  // 项目相关条件
  const projConditions: ConditionBoxConfig = withProj
    ? [
        {
          title: '项目',
          dataIndex: 'project*id',
          atom: 'equal',
          type: 'select',
          dict: {
            data: API_GY_PROJ_SIMPLE_LIST,
            nameKey: 'projectName',
            valueKey: 'id'
          }
        },
        {
          title: '项目类型',
          dataIndex: 'project*projectStateOwnedType',
          atom: 'equal',
          type: 'select',
          dict: {
            data: dictStore.category.get('projectStateOwnedType')
          }
        }
      ]
    : []
  const res: ConditionBoxConfig = [
    ...projConditions,
    {
      title: '档案编号',
      dataIndex: 'id',
      atom: 'like'
    },
    {
      title: '房屋类型',
      dataIndex: 'projectFileUserHouses*houseType',
      atom: 'equal',
      type: 'select',
      dict: {
        data: dictStore.category.get('houseType')
      }
    },
    {
      title: '姓名',
      dataIndex: 'projectStateOwnedMembers*userName',
      atom: 'like'
    },
    {
      title: '身份证号',
      dataIndex: 'projectStateOwnedMembers*userIdCard',
      atom: 'like'
    },
    {
      title: '是否签约',
      dataIndex: 'isSigned',
      atom: 'equal',
      type: 'numberRadio'
    },
    {
      title: '是否打款',
      dataIndex: 'isStart',
      atom: 'equal',
      type: 'numberRadio'
    },
    {
      title: '是否搬离',
      dataIndex: 'isOut',
      atom: 'equal',
      type: 'numberRadio'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      atom: 'between',
      type: 'dateTimeRangePicker'
    },
    {
      title: '审核状态',
      dataIndex: 'shenHeStatus',
      atom: 'equal',
      type: 'select',
      dict: {
        data: ZCH_REVIEW_STATUS_OPTIONS
      }
    }
  ]
  return res
}
